import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Rating,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    useAddGuestReviewMutation,
    useUpdateGuestReviewMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import { GuestReview } from '../../../types/review';

interface GuestReviewModalProps {
    open: boolean;
    onClose: () => void;
    bookingId: number;
    guestName?: string;
    existingReview?: GuestReview;
    readOnly?: boolean;
}

const GuestReviewModal: React.FC<GuestReviewModalProps> = ({
    open,
    onClose,
    bookingId,
    guestName,
    existingReview,
    readOnly = false,
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [rating, setRating] = useState<number | null>(
        existingReview?.rating ?? null,
    );
    const [reviewText, setReviewText] = useState(
        existingReview?.reviewText ?? '',
    );
    const [addGuestReview, { isLoading: isCreating }] =
        useAddGuestReviewMutation();
    const [updateGuestReview, { isLoading: isUpdating }] =
        useUpdateGuestReviewMutation();

    const isPublished = Boolean(existingReview?.published);
    const isReadOnly = readOnly || isPublished;

    useEffect(() => {
        if (open) {
            setRating(existingReview?.rating ?? null);
            setReviewText(existingReview?.reviewText ?? '');
        }
    }, [open, existingReview]);

    const handleSave = async () => {
        if (!rating || !reviewText.trim()) return;

        try {
            if (existingReview?.id) {
                await updateGuestReview({
                    id: existingReview.id,
                    data: { rating, reviewText: reviewText.trim() },
                }).unwrap();
            } else {
                await addGuestReview({
                    booking: { id: bookingId },
                    rating,
                    reviewText: reviewText.trim(),
                }).unwrap();
            }
            onClose();
            toast.success('Review saved successfully');
        } catch {
            toast.error('Failed to save the review. Please try again.');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: { borderRadius: isMobile ? 0 : 2 },
            }}
        >
            <DialogTitle component='div'>
                <div>
                    <Typography variant='inherit' component='h1'>
                        {isReadOnly
                            ? 'Guest review'
                            : existingReview
                              ? 'Edit guest review'
                              : 'Review guest'}
                    </Typography>
                    {guestName ? (
                        <Typography variant='body3' color='text.secondary'>
                            {guestName}
                        </Typography>
                    ) : null}
                </div>
                <IconButton onClick={onClose} size='small' aria-label='Close'>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Box>
                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                            Overall rating
                        </Typography>
                        <Rating
                            value={rating}
                            onChange={(_, value) => setRating(value)}
                            readOnly={isReadOnly}
                            size='large'
                        />
                    </Box>
                    <TextField
                        label='Review'
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        multiline
                        minRows={4}
                        fullWidth
                        InputProps={{ readOnly: isReadOnly }}
                    />
                    {!isPublished ? (
                        <Typography variant='caption' color='text.secondary'>
                            This review will be published 7 days after the
                            booking end date.
                        </Typography>
                    ) : null}
                </Stack>
            </DialogContent>
            {!isReadOnly ? (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={
                            !rating ||
                            !reviewText.trim() ||
                            isCreating ||
                            isUpdating
                        }
                    >
                        {existingReview ? 'Update' : 'Submit'}
                    </Button>
                </DialogActions>
            ) : null}
        </Dialog>
    );
};

export default GuestReviewModal;
